// 影像（cluster 0x8070, spec 21 §3.3）+ 燈光/夜視（cluster 0x8071, §3.4）。
// SleepMode is the only confirm-gated toggle here（單次確認：停止串流與偵測）。

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IMAGE_AI_OVERLAY, IMAGE_FLIP, IMAGE_SLEEP, IMAGE_WATERMARK, LIGHT_NIGHTMODE, LIGHT_STATUS, LIGHT_WARNING } from './ids';
import { useAttr } from './ops';
import { ConfirmDialog, SectionCard, SegmentedRow, ToggleRow } from './widgets';

export function ImageSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const flip = useAttr(deviceId, IMAGE_FLIP, wsOpen);
  const watermark = useAttr(deviceId, IMAGE_WATERMARK, wsOpen);
  const detectOverlay = useAttr(deviceId, IMAGE_AI_OVERLAY, wsOpen); // CAM-5
  const sleep = useAttr(deviceId, IMAGE_SLEEP, wsOpen);
  const [confirmSleep, setConfirmSleep] = useState(false);

  if (!wsOpen) {
    return (
      <SectionCard title={t('deviceSettings.image')}>
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('deviceSettings.connecting')}
        </p>
      </SectionCard>
    );
  }

  const sleepOn = sleep.value === true || sleep.value === 1;

  return (
    <SectionCard title={t('deviceSettings.image')}>
      <ToggleRow label={t('deviceSettings.imageFlip')} hint={t('deviceSettings.imageFlipHint')} attr={flip} />
      <ToggleRow label={t('deviceSettings.watermark')} attr={watermark} />
      {/* CAM-5 偵測框顯示：capability-gated（未宣告 0x8070/0300 的機型隱藏；對齊 Android/iOS 的 unavailable 隱藏）。 */}
      {detectOverlay.status !== 'unavailable' && (
        <ToggleRow label={t('deviceSettings.aiOverlay')} hint={t('deviceSettings.aiOverlayHint')} attr={detectOverlay} />
      )}
      {/* SleepMode: 開啟需單次確認（§3.3）；以自管 row 攔截開啟動作 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', padding: 'var(--spacing-4) 0' }}>
        <div>
          <div>{t('deviceSettings.sleepMode')}</div>
          <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
            {sleep.setError ?? t('deviceSettings.sleepModeHint')}
          </div>
        </div>
        {sleep.status === 'unavailable' ? (
          <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
            {t('deviceSettings.cannotRead')}
          </span>
        ) : (
          <button
            role="switch"
            aria-checked={sleepOn}
            aria-label={t('deviceSettings.sleepMode')}
            disabled={sleep.status !== 'ready' || sleep.setting}
            onClick={() => {
              if (!sleepOn) setConfirmSleep(true);
              else sleep.set(false);
            }}
            style={{
              width: 44,
              height: 26,
              borderRadius: 'var(--radius-full)',
              border: '1px solid var(--color-surface-line)',
              background: sleepOn ? 'var(--color-brand-primary)' : 'var(--color-surface-panel2)',
              position: 'relative',
              opacity: sleep.status !== 'ready' ? 'var(--opacity-disabled)' : 1,
            }}
          >
            <span style={{ position: 'absolute', top: 2, left: sleepOn ? 20 : 2, width: 20, height: 20, borderRadius: '50%', backgroundColor: '#fff' }} />
          </button>
        )}
      </div>
      {confirmSleep && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.sleepConfirmTitle'),
            body: t('deviceSettings.sleepConfirmBody'),
            confirmLabel: t('deviceSettings.turnOn'),
          }}
          onCancel={() => setConfirmSleep(false)}
          onConfirm={() => {
            setConfirmSleep(false);
            sleep.set(true);
          }}
        />
      )}
    </SectionCard>
  );
}

export function LightSection({ deviceId, wsOpen }: { deviceId: string; wsOpen: boolean }) {
  const { t } = useTranslation();
  const statusLight = useAttr(deviceId, LIGHT_STATUS, wsOpen);
  const nightMode = useAttr(deviceId, LIGHT_NIGHTMODE, wsOpen);
  const warning = useAttr(deviceId, LIGHT_WARNING, wsOpen);

  if (!wsOpen) {
    return (
      <SectionCard title={t('deviceSettings.lightNightVision')}>
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('deviceSettings.connecting')}
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={t('deviceSettings.lightNightVision')}>
      <ToggleRow label={t('deviceSettings.statusLight')} attr={statusLight} />
      <SegmentedRow
        label={t('deviceSettings.irNightVision')}
        attr={nightMode}
       
        options={[
          [0, t('common.off')],
          [1, t('common.on')],
        ]}
      />
      <ToggleRow label={t('deviceSettings.warningLight')} attr={warning} />
    </SectionCard>
  );
}
